import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, MaskedLine, usePageMeta } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CEREMONY_TYPES = [
  "Wedding",
  "Commitment Ceremony",
  "Vow Renewal",
  "Elopement",
  "Micro-Wedding",
  "Baby Naming",
  "Baby Blessing",
  "Funeral Service",
  "Celebration of Life",
  "Memorial Service",
  "Handfasting",
  "Coming of Age",
  "Retirement",
  "House Blessing",
  "Other / Not Sure",
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  ceremony_type: "Wedding",
  timeframe: "",
  location: "",
  referral: "",
  message: "",
};

const fieldLabel = "mb-2 block text-[11px] uppercase tracking-[0.22em] text-forest";
const inputClass =
  "w-full border border-forest/25 bg-cream px-4 py-3.5 text-sm text-ink placeholder:text-slate2/50 outline-none transition-colors duration-300 focus:border-gold";

const Contact = () => {
  usePageMeta(
    "Enquire | Scottish Voice and Verse Celebrancy",
    "Tell Karen about your ceremony. Get in touch for a no-obligation conversation about weddings, funerals, naming ceremonies, or any life milestone."
  );

  const [form, setForm] = useState(() => {
    const base = { ...initialForm };
    try {
      const params = new URLSearchParams(window.location.search);
      const ceremony = params.get("ceremony");
      const type = params.get("type");
      if (type && CEREMONY_TYPES.includes(type)) base.ceremony_type = type;
      if (ceremony) base.message = `I would like to enquire about ${ceremony}.`;
    } catch (e) {}
    return base;
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Your enquiry has been sent. Karen will be in touch soon.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please email Karen directly instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="grain relative overflow-hidden bg-forestdeep pb-20 pt-40 text-cream lg:pb-28 lg:pt-52" data-testid="contact-hero">
        <div className="mx-auto max-w-7xl px-6 sm:px-12">
          <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">Contact</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl" data-testid="contact-heading">
            <MaskedLine delay={0.2}>Begin the Conversation</MaskedLine>
          </h1>
          <Reveal delay={0.5}>
            <p className="font-display mt-8 max-w-2xl text-xl italic text-cream/80 sm:text-2xl" data-testid="contact-intro">
              Thinking about your ceremony? Let's have a conversation. Tell me a little about what you are planning:
              your date, your location, the people involved. You do not need to have everything worked out yet. We can
              figure it out together.
            </p>
            <span className="gold-rule mt-10 block" />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:py-28">
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="mb-10 border-l-2 border-gold bg-creamdeep/50 p-6" data-testid="deposit-note">
              <p className="text-sm leading-relaxed text-slate2">
                Ready to confirm your date? You can pay your £100 booking deposit securely here.
              </p>
              <a
                href="https://buy.stripe.com/7sYeV66xvbnPbbD5PQ8Zq00"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-deposit-button"
                className="group mt-4 inline-flex items-center gap-3 bg-gold px-8 py-3.5 text-[12px] uppercase tracking-[0.25em] text-forestdeep transition-colors duration-500 hover:bg-forest hover:text-cream"
              >
                Secure Your Date
                <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </a>
            </div>
            {sent ? (
              <div className="border border-gold/50 bg-creamdeep/50 p-12 text-center" data-testid="enquiry-success-panel">
                <CheckCircle2 size={40} className="mx-auto text-forest" />
                <h2 className="font-display mt-6 text-3xl text-forest">Thank you.</h2>
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate2">
                  Karen reads every enquiry personally and aims to respond within 24 hours. If the matter is urgent,
                  you are welcome to email her directly.
                </p>
                <button
                  onClick={() => {
                    setSent(false);
                    setForm(initialForm);
                  }}
                  data-testid="send-another-button"
                  className="mt-8 text-[11px] uppercase tracking-[0.25em] text-mulberry underline-offset-4 hover:underline"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="enquiry-form" className="space-y-7">
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className={fieldLabel}>
                      Your name <span className="text-mulberry">*</span>
                    </label>
                    <input
                      id="name"
                      data-testid="enquiry-name-input"
                      required
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your full name"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className={fieldLabel}>
                      Email address <span className="text-mulberry">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="enquiry-email-input"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className={fieldLabel}>
                      Phone number <span className="normal-case text-slate2/60">(optional)</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      data-testid="enquiry-phone-input"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="Your phone number"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="ceremony" className={fieldLabel}>
                      Ceremony type
                    </label>
                    <select
                      id="ceremony"
                      data-testid="enquiry-ceremony-select"
                      value={form.ceremony_type}
                      onChange={set("ceremony_type")}
                      className={`${inputClass} appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%232C4A3E%22 stroke-width=%222%22%3E%3Cpath d=%22m6 9 6 6 6-6%22/%3E%3C/svg%3E')] bg-[position:right_1rem_center] bg-no-repeat pr-10`}
                    >
                      {CEREMONY_TYPES.map((t) => (
                        <option key={t} value={t} data-testid={`ceremony-option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="timeframe" className={fieldLabel}>
                    When are you thinking? <span className="normal-case text-slate2/60">Even a rough idea is helpful.</span>
                  </label>
                  <input
                    id="timeframe"
                    data-testid="enquiry-timeframe-input"
                    value={form.timeframe}
                    onChange={set("timeframe")}
                    placeholder="A date, a season, or simply a time of year"
                    className={inputClass}
                  />
                </div>
                <div className="grid gap-7 sm:grid-cols-2">
                  <div>
                    <label htmlFor="location" className={fieldLabel}>
                      Location or venue <span className="normal-case text-slate2/60">(optional)</span>
                    </label>
                    <input
                      id="location"
                      data-testid="enquiry-location-input"
                      value={form.location}
                      onChange={set("location")}
                      placeholder="Where the ceremony might take place"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="referral" className={fieldLabel}>
                      How did you hear about us? <span className="normal-case text-slate2/60">(optional)</span>
                    </label>
                    <input
                      id="referral"
                      data-testid="enquiry-referral-input"
                      value={form.referral}
                      onChange={set("referral")}
                      placeholder="A friend, a venue, a search"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className={fieldLabel}>
                    Tell me a little about your ceremony <span className="normal-case text-slate2/60">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    data-testid="enquiry-message-input"
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="As much or as little as you like. There is no wrong answer here."
                    className={`${inputClass} resize-y`}
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="enquiry-submit-button"
                    className="group inline-flex items-center gap-3 bg-forest px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry disabled:opacity-60"
                  >
                    {sending ? "Sending" : "Send Your Enquiry"}
                    <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
                  </button>
                  <p className="mt-5 text-sm text-slate2" data-testid="response-time-note">
                    Karen reads every enquiry personally and aims to respond to all enquiries within 24 hours.
                  </p>
                </div>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <aside className="space-y-10 lg:pl-8">
              <div className="border-l-2 border-gold pl-6" data-testid="location-note">
                <MapPin size={20} className="mb-4 text-mulberry" />
                <h3 className="font-display text-2xl text-forest">Where Karen works</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate2">
                  Based in Alyth, Perthshire. Serving Perth and Kinross and surrounding areas.
                </p>
              </div>
              <div className="border-l-2 border-gold pl-6" data-testid="direct-contact-note">
                <Mail size={20} className="mb-4 text-mulberry" />
                <h3 className="font-display text-2xl text-forest">Directly</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate2">You can also reach Karen directly at</p>
                <a
                  href="mailto:karenwoods@scottishvoiceandversecelebrancy.co.uk"
                  data-testid="direct-email-link"
                  className="mt-2 block w-fit break-all text-sm text-mulberry underline-offset-4 transition-colors duration-300 hover:text-forest hover:underline"
                >
                  karenwoods@scottishvoiceandversecelebrancy.co.uk
                </a>
              </div>
              <div className="bg-forestdeep p-8 text-cream" data-testid="contact-aside-quote">
                <p className="font-display text-xl italic leading-relaxed text-cream/90">
                  "If the words matter, so does the voice that speaks them."
                </p>
                <span className="mt-5 block h-px w-12 bg-gold" />
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
